/**
 * useOperationalStaffing — shared, staged CRUD hook over a submission's
 * Operational staffing-ratio rows.
 *
 * Mirrors `useLossRuns` / `useFacilities`: a module-level pubsub keyed
 * by `submissionId` so every consumer of the same submission re-renders
 * on any edit. The hook supports an optional `year` filter — the page's
 * year tabs ask for one year at a time, but `save()` flushes every
 * staged change across all years.
 *
 * Data source: the real Orion v2 backend (`/v2/staffing-ratios/`) via
 * `endpoints/operational.ts` — no mock layer.
 *
 * Staged editing (matches the other submission sections):
 *   - `create` / `update` / `remove` mutate ONLY the in-memory working
 *     copy — nothing hits the network.
 *   - `save()` diffs the working copy against the last-saved baseline
 *     and flushes every change (POST new rows, PATCH changed rows,
 *     DELETE removed rows).
 *   - `isDirty` reports whether there are unsaved changes.
 *
 * State machine:
 *   { status: "loading" }
 *   → { status: "ready", data: StaffingRatio[] }
 *   → { status: "error", error: Error }
 */
import { useCallback, useEffect, useState } from "react";
import {
    createStaffingRatio as createApi,
    deleteStaffingRatio as deleteApi,
    listStaffingRatios as listApi,
    patchStaffingRatio as patchApi,
} from "../api";
import type { StaffingRatio, StaffingRatioWritePayload } from "../types/operational";

export type StaffingRatiosQueryState =
    | { status: "loading" }
    | { status: "ready"; data: StaffingRatio[] }
    | { status: "error"; error: Error };

export interface UseOperationalStaffingResult {
    state: StaffingRatiosQueryState;
    /** Stage a new row locally (temp id). Not persisted until `save()`. */
    create: (payload?: StaffingRatioWritePayload) => Promise<StaffingRatio | null>;
    /** Stage a field change locally. Not persisted until `save()`. */
    update: (id: string, payload: StaffingRatioWritePayload) => Promise<void>;
    /** Stage a row removal locally. Not persisted until `save()`. */
    remove: (id: string) => Promise<void>;
    /** Flush every staged change to the backend. */
    save: () => Promise<boolean>;
    /** Re-fetch the staffing ratios from the backend in the background
     *  (no "loading" flash). Used to re-sync when navigating into the
     *  Operational section, since facilities added/deleted in Schedule
     *  change the backend-derived staffing rows (OR-1297). */
    reload: () => Promise<void>;
    /** True when the working copy differs from the last-saved state. */
    isDirty: boolean;
}

const DRAFT_SCOPE = "draft";
const TEMP_ID_PREFIX = "local-";

const WRITABLE_KEYS: (keyof StaffingRatioWritePayload)[] = [
    "facilityId",
    "year",
    "rnStaffing",
    "rnStAvg",
    "lpnStaffing",
    "lpnStAvg",
    "cnaStaffing",
    "cnaStAvg",
    "totalStaffing",
    "totalStAvg",
];

const stateByKey = new Map<string, StaffingRatiosQueryState>();
const baselineByKey = new Map<string, StaffingRatio[]>();
const subscribersByKey = new Map<string, Set<(s: StaffingRatiosQueryState) => void>>();
const inflightLoad = new Map<string, Promise<void>>();

function scopeOf(submissionId: string): string {
    return submissionId.trim() || DRAFT_SCOPE;
}

function isTempId(id: string): boolean {
    return id.startsWith(TEMP_ID_PREFIX);
}

function snapshot(rows: StaffingRatio[]): StaffingRatio[] {
    return rows.map((r) => ({ ...r }));
}

function sameData(a: StaffingRatio[], b: StaffingRatio[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

function toWritePayload(r: StaffingRatio): StaffingRatioWritePayload {
    const out: Record<string, unknown> = {};
    for (const k of WRITABLE_KEYS) out[k] = r[k];
    return out as StaffingRatioWritePayload;
}

function diffPayload(base: StaffingRatio, cur: StaffingRatio): StaffingRatioWritePayload {
    const diff: Record<string, unknown> = {};
    for (const k of WRITABLE_KEYS) {
        if (base[k] !== cur[k]) diff[k] = cur[k];
    }
    return diff as StaffingRatioWritePayload;
}

function emit(key: string, next: StaffingRatiosQueryState): void {
    stateByKey.set(key, next);
    const subs = subscribersByKey.get(key);
    if (!subs) return;
    subs.forEach((cb) => cb(next));
}

function subscribe(key: string, cb: (s: StaffingRatiosQueryState) => void): () => void {
    let set = subscribersByKey.get(key);
    if (!set) {
        set = new Set();
        subscribersByKey.set(key, set);
    }
    set.add(cb);
    return () => {
        set!.delete(cb);
    };
}

function mutateData(key: string, transform: (prev: StaffingRatio[]) => StaffingRatio[]): void {
    const current = stateByKey.get(key);
    if (current?.status !== "ready") return;
    emit(key, { status: "ready", data: transform(current.data) });
}

function load(submissionId: string): Promise<void> {
    const key = scopeOf(submissionId);
    const existing = inflightLoad.get(key);
    if (existing) return existing;
    const promise = listApi(submissionId)
        .then((data) => {
            baselineByKey.set(key, snapshot(data));
            emit(key, { status: "ready", data });
        })
        .catch((error: unknown) => {
            emit(key, {
                status: "error",
                error: error instanceof Error ? error : new Error("Failed to load staffing ratios"),
            });
        })
        .finally(() => {
            inflightLoad.delete(key);
        });
    inflightLoad.set(key, promise);
    return promise;
}

export function useOperationalStaffing(submissionId: string | undefined): UseOperationalStaffingResult {
    const key = submissionId === undefined ? null : scopeOf(submissionId);

    const [state, setState] = useState<StaffingRatiosQueryState>(() => {
        if (key === null) return { status: "loading" };
        return stateByKey.get(key) ?? { status: "loading" };
    });

    useEffect(() => {
        if (key === null || submissionId === undefined) return;
        const unsubscribe = subscribe(key, setState);
        const cached = stateByKey.get(key);
        if (cached) setState(cached);
        if (!cached || cached.status === "error") {
            emit(key, { status: "loading" });
            void load(submissionId);
        }
        return unsubscribe;
    }, [key, submissionId]);

    const create = useCallback<UseOperationalStaffingResult["create"]>(
        async (payload = {}) => {
            if (submissionId === undefined || key === null) return null;
            const row: StaffingRatio = {
                id: `${TEMP_ID_PREFIX}${crypto.randomUUID()}`,
                submissionId,
                facilityId: "",
                facilityName: "",
                address: "",
                city: "",
                state: "",
                year: 0,
                rnStaffing: "",
                rnStAvg: "",
                lpnStaffing: "",
                lpnStAvg: "",
                cnaStaffing: "",
                cnaStAvg: "",
                totalStaffing: "",
                totalStAvg: "",
                createdAt: "",
                updatedAt: "",
                ...payload,
            };
            mutateData(key, (prev) => [...prev, row]);
            return row;
        },
        [submissionId, key],
    );

    const update = useCallback<UseOperationalStaffingResult["update"]>(
        async (id, payload) => {
            if (key === null) return;
            mutateData(key, (prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
        },
        [key],
    );

    const remove = useCallback<UseOperationalStaffingResult["remove"]>(
        async (id) => {
            if (key === null) return;
            mutateData(key, (prev) => prev.filter((r) => r.id !== id));
        },
        [key],
    );

    const save = useCallback<UseOperationalStaffingResult["save"]>(async () => {
        if (submissionId === undefined || key === null) return false;
        const current = stateByKey.get(key);
        if (current?.status !== "ready") return true;
        const working = current.data;
        const baseline = baselineByKey.get(key) ?? [];
        if (sameData(working, baseline)) return true;

        try {
            const deletes = baseline.filter((b) => !working.some((w) => w.id === b.id));
            await Promise.all(deletes.map((d) => deleteApi(submissionId, d.id)));

            const nextRows: StaffingRatio[] = [];
            for (const row of working) {
                if (isTempId(row.id)) {
                    const created = await createApi(submissionId, toWritePayload(row));
                    nextRows.push({
                        ...row,
                        id: created.id,
                        createdAt: created.createdAt,
                        updatedAt: created.updatedAt,
                    });
                    continue;
                }
                const base = baseline.find((b) => b.id === row.id);
                const diff = base ? diffPayload(base, row) : {};
                if (Object.keys(diff).length > 0) {
                    const patched = await patchApi(submissionId, row.id, diff);
                    nextRows.push({ ...row, updatedAt: patched.updatedAt });
                } else {
                    nextRows.push(row);
                }
            }

            baselineByKey.set(key, snapshot(nextRows));
            emit(key, { status: "ready", data: nextRows });
            return true;
        } catch (error) {
            console.warn("saveOperationalStaffing failed:", error);
            return false;
        }
    }, [submissionId, key]);

    // Background refetch — reuses `load()` (dedups via `inflightLoad` and
    // emits "ready" without a "loading" flash), so navigating back into the
    // section silently re-syncs the table instead of showing stale data.
    const reload = useCallback<UseOperationalStaffingResult["reload"]>(async () => {
        if (submissionId === undefined || key === null) return;
        await load(submissionId);
    }, [submissionId, key]);

    const baseline = key === null ? undefined : baselineByKey.get(key);
    const isDirty = state.status === "ready" && baseline !== undefined ? !sameData(state.data, baseline) : false;

    return { state, create, update, remove, save, reload, isDirty };
}
